import { useMemo, useState } from "react";
import { ProductCatalog } from "types/productCatalog";
import { useSearchParam } from "hooks/useSearchParam";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import useSWR from "swr";
import { fetcher } from "services/api.service";
import { Box, Button, MenuItem, Paper, Select, Typography } from "@mui/material";
import { parseInt } from "lodash";
import ProductCatalogsDataGrid from "components/ProductCatalogs/ProductCatalogsDataGrid";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { PageActions, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { PagePaperPreamble } from "pages/Layouts/components/PagePaperPreamble";
import { warehouses } from "../../constants/warehouses";

export const WholesalePage = () => {
  const [wholesaleProductCatalogs, setWholesaleProductCatalogs] = useState<ProductCatalog[]>([]);
  const [warehouseId, setWarehouseId] = useSearchParam("warehouse_id", null, Number);
  const location = useLocation();
  const { data } = useSWR<{ productCatalogs: ProductCatalog[]; meta: { count: number } }>(
    () => "product_catalogs?filter[]=quantity,>,0" + location.search.replace("?", "&"),
    fetcher,
  );
  const showSelectAllBtn = useMemo(() => {
    return (
      data?.productCatalogs.filter(({ quantity }) => quantity > 0).length ===
      wholesaleProductCatalogs.filter(({ quantity }) => quantity > 0).length
    );
  }, [data?.productCatalogs, wholesaleProductCatalogs]);

  return (
    <FullPagePaperLayout>
      <Outlet context={{ wholesaleProductCatalogs, totalProductCatalogs: data?.meta.count }} />
      <PageBreadcrumbs links={[{ label: "Resale" }, { label: "Product Catalog", path: "/product-catalog" }]} />
      <PageTitle>Reserve Wholesale Lot</PageTitle>
      <PageSubtitle>Filter and select the items you'd like to reserve for wholesale</PageSubtitle>

      <PageActions>
        <Button variant="outlined" component={RouterLink} to="/product-catalog">
          Exit
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ ml: 1 }}
          disabled={wholesaleProductCatalogs.length < 1}
          component={RouterLink}
          to={{
            pathname: "confirm",
            search: `${location.search}`,
          }}
        >
          Create Lot
        </Button>
      </PageActions>
      <PagePaperPreamble>
        <Box width="100%">
          {showSelectAllBtn && (
            <Box sx={{ background: "rgb(227, 242, 253)" }} display="flex" justifyContent="center">
              <Button
                variant="text"
                color="primary"
                sx={{ ml: 1 }}
                component={RouterLink}
                to={{
                  pathname: "confirmAll",
                  search: `${location.search}`,
                }}
              >
                Select ALL {data?.meta.count} Product Catalogs
              </Button>
            </Box>
          )}

          <Paper sx={{ padding: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>Filters: </Typography>
              <Select
                value={warehouseId}
                size="small"
                label="Warehouse"
                onChange={(e) => setWarehouseId(parseInt((e.target.value || "").toString()))}
              >
                {warehouses
                  .filter((warehouse) => warehouse.isRevivn)
                  .map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>Products Selected: {wholesaleProductCatalogs.length}</Typography>
              <Typography>
                Units Selected:{" "}
                {wholesaleProductCatalogs.reduce(
                  (acc, pc) => acc + (pc.wholesaleQuantity || pc.warehouseQuantity || pc.quantity),
                  0,
                )}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </PagePaperPreamble>
      {warehouseId && (
        <ProductCatalogsDataGrid wholesaleMode={true} setWholesaleProductCatalogs={setWholesaleProductCatalogs} />
      )}
    </FullPagePaperLayout>
  );
};
